import { t } from "@lingui/macro";
import {
  type BudgetovacPositionPriceFragment,
  type BudgetovacPositionPriceInput,
  BudgetovacPositionsSetDocument,
  type BudgetovacPositionsSetFragment,
  BudgetovacPositionsSetModalDependenciesDocument,
  type BudgetovacPositionsSetModalDependenciesQuery,
  type BudgetovacPositionsSetModalDependenciesQueryVariables,
  type BudgetovacPositionsSetQuery,
  type BudgetovacPositionsSetQueryVariables,
  type CreateBudgetovacPositionMutation,
  CreateBudgetovacPositionsSetDocument,
  type CreateBudgetovacPositionsSetMutation,
  type CreateBudgetovacPositionsSetMutationVariables,
  UpdateBudgetovacPositionsSetDocument,
  type UpdateBudgetovacPositionsSetMutation,
  type UpdateBudgetovacPositionsSetMutationVariables,
} from "@src/__generated__/graphql";
import { BudgetovacPositionsSetModal } from "@src/components/modules/budgets/positions-sets/components/modal";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import type { ModalStore } from "@src/stores/ModalStore";
import type { TToFieldStates } from "@src/utils/forms/ts-utils";
import { arrayLengthMin, gt, required } from "@src/utils/forms/validators";
import mapToOptions from "@src/utils/map-to-options";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { NumberModel } from "@src/utils/models";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

type BudgetovacPositionsSetModalAdditionalData = {
  readonly positionsSet?: BudgetovacPositionsSetFragment;
  readonly positionsSetId?: string;
  readonly clientId?: string;
  readonly onSubmit?: () => void;
  readonly isDuplicate?: boolean;
};

type PositionPriceForm = FormState<
  TToFieldStates<BudgetovacPositionPriceFragment>
>;

export class BudgetovacPositionsSetModalStore implements BaseStore, ModalStore {
  readonly modalId = "budgetovacPositionsSetModal";

  private dependenciesFetcher = new FetchHelper<
    BudgetovacPositionsSetModalDependenciesQuery,
    BudgetovacPositionsSetModalDependenciesQueryVariables
  >(BudgetovacPositionsSetModalDependenciesDocument);

  private detailFetcher = new FetchHelper<
    BudgetovacPositionsSetQuery,
    BudgetovacPositionsSetQueryVariables
  >(BudgetovacPositionsSetDocument);

  private createMutator = new MutationHelper<
    CreateBudgetovacPositionsSetMutation,
    CreateBudgetovacPositionsSetMutationVariables
  >(CreateBudgetovacPositionsSetDocument);

  private updateMutator = new MutationHelper<
    UpdateBudgetovacPositionsSetMutation,
    UpdateBudgetovacPositionsSetMutationVariables
  >(UpdateBudgetovacPositionsSetDocument);

  /**
   * NOTE: Budgetovac has some weird shit going on so we have to use NumberModel the other way around.
   * For commissions we receive nominated values but we need to show them as denominated values
   * and also send them as nominated values in mutations.
   * */
  modalForm = new FormState({
    name: new FieldState("").validators(required),
    active: new FieldState(true),
    currency_code: new FieldState("").validators(required),
    client_id: new FieldState<string | undefined>(undefined),
    internal_production_commission: new FieldState<NumberModel>(
      new NumberModel(0, 2),
    ),
    external_production_commission: new FieldState<NumberModel>(
      new NumberModel(0, 2),
    ),
    media_budget_commission: new FieldState<NumberModel>(new NumberModel(0, 2)),
    extras_commission: new FieldState<NumberModel>(new NumberModel(0, 2)),
    position_prices: new FormState<PositionPriceForm[]>([]),
  }).validators(({ position_prices }) => arrayLengthMin(1)(position_prices.$));

  @observable
  positions: BudgetovacPositionsSetModalDependenciesQuery["budgetovacPositions"] =
    [];

  @observable.ref
  departmentOptions: IOption[] = [];

  @observable.ref
  brandOptions: IOption[] = [];

  @observable.ref
  editingPositionsSetId?: string;

  @observable.ref
  isDuplicateMode = false;

  @observable.ref
  loadedPositionsSet?: BudgetovacPositionsSetFragment;

  modalState = new DisclosureState<BudgetovacPositionsSetModalAdditionalData>({
    onOpen: async (additionalData) => {
      this.appStore.UIStore.dialogs.openModal({
        id: this.modalId,
        content: <BudgetovacPositionsSetModal />,
      });

      await this.fetchDependencies();

      if (additionalData?.isDuplicate && additionalData?.positionsSet) {
        this.fillFormFromPositionsSet(additionalData.positionsSet);
        this.modalForm.$.name.reset(
          `${additionalData.positionsSet.name} - COPY`,
        );
        this.modalForm.$.client_id.onChange(additionalData.clientId);
        this.modalForm.$.active.onChange(true);
        this.isDuplicateMode = true;
      } else if (additionalData?.positionsSet) {
        this.fillFormFromPositionsSet(additionalData.positionsSet);
        this.editingPositionsSetId = additionalData.positionsSet.id;
      } else if (additionalData?.positionsSetId) {
        await this.fetchDetail(additionalData.positionsSetId);
      } else {
        this.initializeCreateForm();
      }
    },
    onClose: () => {
      this.appStore.UIStore.dialogs.closeModal(this.modalId);
      this.modalForm.reset();
      this.editingPositionsSetId = undefined;
      this.loadedPositionsSet = undefined;
      this.isDuplicateMode = false;
    },
  });

  constructor(public appStore: AppStore) {
    makeObservable(this);
  }

  @computed
  get currencyOptions(): IOption[] {
    return this.appStore.workspaceStore.currencies.map((currency) => ({
      label: currency.code,
      value: currency.code,
    }));
  }

  @computed
  get modalTitle(): string {
    if (this.isDuplicateMode) return t`Duplicate rate card`;
    return this.isEditing ? t`Edit rate card` : t`New rate card`;
  }

  @computed
  get isEditing(): boolean {
    return !!this.editingPositionsSetId;
  }

  @computed
  get isLoadingDependencies(): boolean {
    return this.dependenciesFetcher.isLoading.value;
  }

  @computed
  get isLoadingDetail(): boolean {
    return this.detailFetcher.isLoading.value;
  }

  @computed
  get isCreating(): boolean {
    return this.createMutator.isLoading.value;
  }

  @computed
  get isUpdating(): boolean {
    return this.updateMutator.isLoading.value;
  }

  @computed
  get isSubmitting(): boolean {
    return this.isCreating || this.isUpdating;
  }

  @computed
  get availablePositions(): IOption[] {
    const selectedIds = new Set(
      this.modalForm.$.position_prices.$.map(({ $ }) => $.position_id.value),
    );

    return this.positions
      .filter((position) => !selectedIds.has(position.id))
      .map((pos) => ({
        label: pos.name,
        value: pos.id,
      }));
  }

  @action.bound
  async fetchDependencies(): Promise<void> {
    const [data, error] = await this.dependenciesFetcher.fetch();

    if (error || !data) return;

    this.positions = data.budgetovacPositions;
    this.brandOptions = mapToOptions.brands(data.brandsSimpleMap);
    this.departmentOptions = mapToOptions.toOptions(
      data.ourWorkBudgetCategories ?? [],
      "name",
    );
  }

  @action.bound
  async fetchDetail(id: string): Promise<void> {
    const [data, error] = await this.detailFetcher.fetch({ id });

    if (error || !data?.budgetovacPositionsSet) return;

    this.loadedPositionsSet = data.budgetovacPositionsSet;
    this.fillFormFromPositionsSet(data.budgetovacPositionsSet);
    this.editingPositionsSetId = id;
  }

  private createPositionPriceForm(
    price?: BudgetovacPositionPriceFragment,
  ): PositionPriceForm {
    return new FormState<TToFieldStates<BudgetovacPositionPriceFragment>>({
      position_id: new FieldState(price?.position_id ?? "").validators(
        required,
      ),
      name: new FieldState(price?.name ?? "").validators(required),
      alias: new FieldState(price?.alias ?? "").validators(required),
      price: new FieldState(price?.price ?? "").validators(required, gt(0)),
      department_id: new FieldState(price?.department_id),
      department_name: new FieldState(price?.department_name),
    });
  }

  setDenomValueToField(
    field: FieldState<NumberModel>,
    value: string | number,
  ): void {
    field.onChange(NumberModel.fromDenomValue(value, field.value.denominator));
  }

  @action.bound
  fillFormFromPositionsSet(positionsSet: BudgetovacPositionsSetFragment): void {
    this.modalForm.$.name.onChange(positionsSet.name);
    this.modalForm.$.active.onChange(positionsSet.active);
    this.modalForm.$.currency_code.onChange(positionsSet.currency_code);
    this.modalForm.$.client_id.onChange(
      positionsSet.client_id?.toString() ?? undefined,
    );

    NumberModel.setFieldState(
      this.modalForm.$.internal_production_commission,
      positionsSet.internal_production_commission ?? 0,
    );
    NumberModel.setFieldState(
      this.modalForm.$.external_production_commission,
      positionsSet.external_production_commission ?? 0,
    );
    NumberModel.setFieldState(
      this.modalForm.$.media_budget_commission,
      positionsSet.media_budget_commission ?? 0,
    );
    NumberModel.setFieldState(
      this.modalForm.$.extras_commission,
      positionsSet.extras_commission ?? 0,
    );

    this.modalForm.$.position_prices.reset();
    this.modalForm.$.position_prices = new FormState<PositionPriceForm[]>(
      positionsSet.positions_prices.map(this.createPositionPriceForm),
    );
  }

  @action.bound
  initializeCreateForm(): void {
    this.modalForm.$.name.reset("");
    this.modalForm.$.active.onChange(true);
    this.modalForm.$.currency_code.onChange(
      this.appStore.workspaceStore.settings?.currency.code ?? "",
    );
    this.modalForm.$.client_id.onChange(
      this.modalState.additionalData?.clientId,
    );

    NumberModel.setFieldState(
      this.modalForm.$.internal_production_commission,
      0,
    );
    NumberModel.setFieldState(
      this.modalForm.$.external_production_commission,
      0,
    );
    NumberModel.setFieldState(this.modalForm.$.media_budget_commission, 0);
    NumberModel.setFieldState(this.modalForm.$.extras_commission, 0);

    this.modalForm.$.position_prices = new FormState<PositionPriceForm[]>(
      this.positions
        .filter(({ is_default }) => is_default)
        .map((position) =>
          this.createPositionPriceForm({
            position_id: position.id,
            name: position.name,
            alias: position.alias,
            price: position.price.toString(),
            department_id: position.department_id ?? undefined,
            department_name: this.departmentOptions.find(
              (d) => d.value === position.department_id,
            )?.label,
          }),
        ),
    );
  }

  @action.bound
  addPositionRow(positionId: string): void {
    const position = this.positions.find((p) => p.id === positionId);
    if (!position) return;

    const exists = this.modalForm.$.position_prices.$.some(
      ({ $ }) => $.position_id.value === positionId,
    );

    if (exists) return;

    this.modalForm.$.position_prices.$.push(
      this.createPositionPriceForm({
        position_id: position.id,
        name: position.name,
        alias: position.alias,
        price: String(position.price),
        department_id: position.department_id ?? undefined,
        department_name: this.departmentOptions.find(
          (d) => d.value === position.department_id,
        )?.label,
      }),
    );
    this.modalForm.clearFormError();
  }

  @action.bound
  removePositionRow(positionId: string): void {
    const positionIndex = this.modalForm.$.position_prices.$.findIndex(
      ({ $ }) => $.position_id.value === positionId,
    );

    if (positionIndex === -1) return;

    this.modalForm.$.position_prices.$.splice(positionIndex, 1);
  }

  @action.bound
  async onSubmit(): Promise<void> {
    const res = await this.modalForm.validate();
    if (res.hasError) return;

    const positionsPrices: BudgetovacPositionPriceInput[] =
      res.value.position_prices.$.map(({ $ }) => ({
        position_id: $.position_id.value,
        name: $.name.value,
        alias: $.alias.value,
        price: $.price.value,
        department_id: $.department_id.value,
      }));

    const input = {
      name: res.value.name.$,
      active: res.value.active.$,
      currency_code: res.value.currency_code.$,
      client_id: Number(res.value.client_id.$),
      internal_production_commission:
        res.value.internal_production_commission.$.value,
      external_production_commission:
        res.value.external_production_commission.$.value,
      media_budget_commission: res.value.media_budget_commission.$.value,
      extras_commission: res.value.extras_commission.$.value,
      positions_prices: positionsPrices,
    };

    if (this.isEditing && this.editingPositionsSetId) {
      const [, error] = await this.updateMutator.mutate({
        input: {
          ...input,
          id: this.editingPositionsSetId,
        },
      });

      if (error) return;

      this.appStore.UIStore.toast({
        title: t`Rate card updated successfully`,
        status: "success",
      });
    } else {
      const [, error] = await this.createMutator.mutate({
        input,
      });

      if (error) return;

      this.appStore.UIStore.toast({
        title: t`Rate card created successfully`,
        status: "success",
      });
    }

    this.modalState.additionalData?.onSubmit?.();
    this.modalState.onClose();
  }

  @action.bound
  onCreatePosition(
    position: CreateBudgetovacPositionMutation["createBudgetovacPosition"],
  ): void {
    this.positions.unshift(position);
    this.addPositionRow(position.id);
  }
}
